"use client";

import { useState } from "react";
import { usePathname } from "next/navigation";

import { Menu, X } from "lucide-react";

import { CREATE_CAMPAIGN_URL, SIGN_IN_URL } from "@/config/routes";
import { cn } from "@/lib/utils";

const NAV_LINKS = [
  { label: "Product", href: "/product" },
  { label: "Solutions", href: "/solutions" },
  { label: "Pricing", href: "/pricing" },
  { label: "Resources", href: "/resources" },
];

export default function EmbedHeader() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="bg-background/85 sticky top-0 z-50 w-full border-b border-transparent backdrop-blur-md transition-all duration-200">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
        {/* Logo */}
        <a
          aria-label="Folktale home"
          href="/welcome"
          className="flex items-center gap-2.5"
        >
          <img
            src="/assets/folktale-mark-D8FTk878.png"
            alt="Folktale"
            width={28}
            height={28}
            className="rounded-md object-contain"
          />
          <span className="text-xl font-medium tracking-tight">Folktale</span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map(({ label, href }) => {
            const active = pathname === href;
            return (
              <a
                key={href}
                href={href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "hover:bg-secondary hover:text-foreground rounded-md px-3.5 py-2 text-sm font-medium transition-colors",
                  active ? "text-foreground" : "text-foreground/75",
                )}
                style={active ? { color: "#1a4d33" } : undefined}
              >
                {label}
              </a>
            );
          })}
        </nav>

        {/* Desktop actions */}
        <div className="hidden items-center gap-2 lg:flex">
          <a
            href={SIGN_IN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-foreground/75 hover:text-foreground rounded-md px-3 py-2 text-sm font-medium transition-colors"
          >
            Sign in
          </a>
          <a
            href="/contact"
            className="border-foreground bg-background hover:bg-foreground hover:text-background inline-flex h-8 items-center justify-center rounded-md border px-3 text-xs font-medium shadow-sm transition-colors"
          >
            Speak to Sales
          </a>
          <a
            href={CREATE_CAMPAIGN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-8 items-center justify-center rounded-md px-3 text-xs font-medium shadow transition-colors"
            style={{ backgroundColor: "#1a4d33", color: "#f5f0e8" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#c4623a";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#1a4d33";
            }}
          >
            Start a free campaign
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          className="hover:bg-secondary inline-flex h-10 w-10 items-center justify-center rounded-md lg:hidden"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? (
            <X className="h-5 w-5" aria-hidden />
          ) : (
            <Menu className="h-5 w-5" aria-hidden />
          )}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-border bg-background/95 border-t px-5 pt-3 pb-5 lg:hidden">
          <nav className="flex flex-col gap-1">
            {NAV_LINKS.map(({ label, href }) => {
              const active = pathname === href;
              return (
                <a
                  key={href}
                  href={href}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "hover:bg-secondary rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                    active ? "text-foreground" : "text-foreground/75",
                  )}
                  style={active ? { color: "#1a4d33" } : undefined}
                  onClick={() => setMobileOpen(false)}
                >
                  {label}
                </a>
              );
            })}
          </nav>
          <div className="border-border mt-4 flex flex-col gap-2 border-t pt-4">
            <a
              href="https://folktale.io/account/sign-in"
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground/75 hover:bg-secondary hover:text-foreground rounded-md px-3 py-2.5 text-sm font-medium transition-colors"
            >
              Sign in
            </a>
            <a
              href="/contact"
              className="border-foreground bg-background hover:bg-foreground hover:text-background inline-flex h-9 items-center justify-center rounded-md border px-3 text-sm font-medium transition-colors"
            >
              Speak to Sales
            </a>
            <a
              href={CREATE_CAMPAIGN_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-9 items-center justify-center rounded-md px-3 text-sm font-medium"
              style={{ backgroundColor: "#1a4d33", color: "#f5f0e8" }}
            >
              Start a free campaign
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
